import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ApiError } from '../api/client';
import { api, type AdminFunnelDto } from '../api/client';
import { useSession } from '../store/session';

/**
 * Воронка отдельной страницей, а не блоком в админке: у неё свой период, и
 * переключение периода перезагружало бы вместе с ней реестр отчётов и подбор.
 *
 * Цифры приходят с сервера сырыми — доли считаются здесь. Так подпись «57 %»
 * стоит рядом с тем, от чего она берётся, и не превращается в ещё одно поле
 * контракта, которое нечем проверить.
 */
export function AdminFunnel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, locale, ready } = useSession();

  // undefined — «за всё время»: сервер без параметра days считает всю историю.
  const [days, setDays] = useState<number | undefined>(undefined);
  const [data, setData] = useState<AdminFunnelDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((period: number | undefined) => {
    setBusy(true);
    setError(null);
    void api
      .adminFunnel(period)
      .then(setData)
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setBusy(false));
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate('/login?next=/admin/funnel');
      return;
    }
    if (user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    load(days);
  }, [ready, user, navigate, load, days]);

  const when = (iso: string) =>
    new Date(iso).toLocaleDateString(locale === 'kk' ? 'kk-KZ' : 'ru-RU', { dateStyle: 'medium' });

  // Доля от вершины воронки. Ноль сверху — это не «0 %», а «считать не от
  // чего»: у пустой базы прочерк честнее ложного нуля.
  const share = (value: number, base: number) =>
    base > 0 ? `${Math.round((value / base) * 100)} %` : '—';

  // Ширина полоски. Минимум в 2 % оставляет видимый след у ступени, до которой
  // дошёл один человек из тысячи: пустая строка читалась бы как «никого».
  const width = (value: number, base: number) =>
    base > 0 ? `${Math.min(100, Math.max(2, (value / base) * 100))}%` : '0%';

  const periods: Array<{ key: string; value: number | undefined }> = [
    { key: 'periodWeek', value: 7 },
    { key: 'periodMonth', value: 30 },
    { key: 'periodQuarter', value: 90 },
    { key: 'periodAll', value: undefined },
  ];

  /**
   * Ступени в порядке пути человека. `base` у каждой — то число, долей от
   * которого она осмысленна: доля скачавших PDF считается от пар с готовым
   * разбором, а не от всех зарегистрированных, иначе получается «1 % скачал»
   * там, где на деле скачал каждый второй, кому было что скачивать.
   */
  const steps = data
    ? [
        { key: 'registered', value: data.registered, base: data.registered },
        { key: 'testStarted', value: data.testStarted, base: data.registered },
        { key: 'testAbandoned', value: data.testAbandoned, base: data.testStarted },
        { key: 'testCompleted', value: data.testCompleted, base: data.testStarted },
        { key: 'invitesCreated', value: data.invitesCreated, base: data.testCompleted },
        { key: 'invitesAccepted', value: data.invitesAccepted, base: data.invitesCreated },
        { key: 'pairsBothDone', value: data.pairsBothDone, base: data.invitesAccepted },
        { key: 'pdfDownloaders', value: data.pdfDownloaders, base: data.pairsBothDone },
        { key: 'paidPairs', value: data.paidPairs, base: data.pairsBothDone },
        { key: 'grantedPairs', value: data.grantedPairs, base: data.pairsBothDone },
      ]
    : [];

  return (
    <div className="page">
      <div className="stack">
        <h1>{t('funnel.title')}</h1>
        <p className="muted">{t('funnel.lead')}</p>

        <div className="btn-row">
          {periods.map((p) => (
            <button
              key={p.key}
              className={`btn ${days === p.value ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setDays(p.value)}
              disabled={busy}
            >
              {t(`funnel.${p.key}`)}
            </button>
          ))}
        </div>

        <Link to="/admin" className="fine">
          ← {t('nav.admin')}
        </Link>
      </div>

      {error && (
        <div className="card">
          <p style={{ color: 'var(--alert)' }}>{error}</p>
        </div>
      )}

      {!error && data === null && <p className="muted">{t('common.loading')}</p>}

      {!error && data && (
        <>
          <div className="card stack">
            <p className="fine">
              {data.from
                ? `${when(data.from)} — ${when(data.to)}`
                : `${t('funnel.periodAll')} · ${when(data.to)}`}
            </p>

            {steps.map((step) => (
              <div className="meter" key={step.key}>
                <span className="meter-label">
                  <span>{t(`funnel.steps.${step.key}`)}</span>
                  <b>
                    {step.value} · {share(step.value, step.base)}
                  </b>
                </span>
                <i>
                  <em style={{ width: width(step.value, step.base) }} />
                </i>
              </div>
            ))}
          </div>

          <div className="card stack">
            <h2>{t('funnel.detailsTitle')}</h2>
            <p className="fine">
              {t('funnel.pdfDownloads')}: {data.pdfDownloads}
            </p>
            <p className="fine">{t('funnel.pdfHint')}</p>
            <p className="fine">{t('funnel.copyHint')}</p>
            <p className="fine">{t('funnel.grantHint')}</p>
          </div>
        </>
      )}
    </div>
  );
}
